import {
  InvoiceCollectionPath,
  InvoiceKind,
  InvoiceStatus,
} from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import { formatInvoicePeriod } from "@/components/billing-plan/billing-plan-values";

export type UnitInvoices = RouterOutputs["adminInvoices"]["getUnitInvoices"];
export type UnitInvoice = UnitInvoices["invoices"][number];
export type PendingCycle = UnitInvoices["pendingCycles"][number];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: "Draft",
  [InvoiceStatus.Open]: "Open",
  [InvoiceStatus.Paid]: "Paid",
  [InvoiceStatus.Void]: "Void",
  [InvoiceStatus.Uncollectible]: "Uncollectible",
};

export const COLLECTION_PATH_LABELS: Record<InvoiceCollectionPath, string> = {
  [InvoiceCollectionPath.ProcessorInvoice]: "Emailed invoice, paid online",
  [InvoiceCollectionPath.DirectCharge]: "Charged to the saved payment method",
};

export function isDirectCharge(invoice: {
  collectionPath: InvoiceCollectionPath | null;
}): boolean {
  return invoice.collectionPath === InvoiceCollectionPath.DirectCharge;
}

export function invoiceStatusVariant(status: InvoiceStatus) {
  if (status === InvoiceStatus.Paid) {
    return "default" as const;
  }
  if (status === InvoiceStatus.Draft) {
    return "secondary" as const;
  }
  if (status === InvoiceStatus.Open) {
    return "outline" as const;
  }
  return "destructive" as const;
}

type InvoiceWindows = {
  kind: InvoiceKind;
  cycleStart: string;
  cycleEnd: string;
  usagePeriodStart: string | null;
  usagePeriodEnd: string | null;
};

/**
 * What an invoice bills, one line per period. A cycle invoice names its base
 * fee period and, when it differs, the usage period it carries; a closing
 * invoice has only usage.
 */
export function describeInvoiceCoverage(invoice: InvoiceWindows): string[] {
  const cycle = formatInvoicePeriod({
    start: invoice.cycleStart,
    end: invoice.cycleEnd,
  });
  const usage =
    invoice.usagePeriodStart && invoice.usagePeriodEnd
      ? formatInvoicePeriod({
          start: invoice.usagePeriodStart,
          end: invoice.usagePeriodEnd,
        })
      : null;
  if (invoice.kind === InvoiceKind.Closing) {
    return [`Usage ${usage ?? cycle}`];
  }
  if (usage === null || usage === cycle) {
    return [cycle];
  }
  return [`Base fee ${cycle}`, `Usage ${usage}`];
}
